import { Dispatch, SetStateAction, useEffect } from 'react';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { Button, Card, Input, Select, Tooltip } from '@iwoca/orion';
import clsx from 'clsx';
import { FileUpload } from 'components/Registration/FileUpload/FileUpload';
import { DOCUMENT_UPLOAD_UK } from 'components/Registration/FileUpload/FileUpload.helpers';
import { TIntroducerType } from 'components/Registration/registration.types';
import { CompanyInputUK } from 'components/Signup/NewSignupForm/CompanyInputUK/CompanyInputUK';
import { useFormContext } from 'react-hook-form';
import { FieldError } from '../FieldError/FieldError';
import sharedStyles from '../RegistrationForm.module.css';
import { TRegistrationFormValues } from '../registrationForm.types';
import { CommercialFinance } from './CommercialFinance/CommercialFinance';
import styles from './CompanyInformation.module.css';

type TProps = {
  introducerType?: TIntroducerType;
  isCompanySearch: boolean;
  selectedCompany?: GetCompanySearchResponse['data'][number];
  setSelectedCompany: Dispatch<
    SetStateAction<GetCompanySearchResponse['data'][number] | undefined>
  >;
  setIntroducerType: Dispatch<SetStateAction<TIntroducerType | undefined>>;
  setIsCompanySearch: Dispatch<SetStateAction<boolean>>;
};

export const CompanyInformation = ({
  introducerType,
  isCompanySearch,
  selectedCompany,
  setSelectedCompany,
  setIntroducerType,
  setIsCompanySearch,
}: TProps) => {
  const {
    register,
    setValue,
    trigger,
    watch,
    formState: { isSubmitted, errors },
  } = useFormContext<TRegistrationFormValues>();

  const handleToggleCompanyInputMode = () => {
    setIsCompanySearch(!isCompanySearch);

    // Unset values
    setValue('companyName', '');
    setValue('companyNumber', '');
    setValue('firstName', '');
    setValue('lastName', '');
  };

  const handleChangeIntroducerType = (newIntroducerType: TIntroducerType) => {
    if (
      (newIntroducerType === 'accountant' && isCompanySearch) ||
      (newIntroducerType === 'broker' && !isCompanySearch)
    ) {
      handleToggleCompanyInputMode();
    }
    setIntroducerType(newIntroducerType);
  };

  const handleCompanySearchSelect = (
    company?: GetCompanySearchResponse['data'][number],
  ) => {
    setSelectedCompany(company);
    setValue('companyName', company?.name || '');
    setValue('companyNumber', company?.number || '');

    // Unset director
    setValue('firstName', '');
    setValue('lastName', '');

    if (isSubmitted) {
      trigger();
    }
  };

  // if user clears the company search using the cross icon, ensure the fields are cleared
  useEffect(() => {
    if (!selectedCompany) {
      setValue('companyName', '');
      setValue('companyNumber', '');
      setValue('firstName', '');
      setValue('lastName', '');

      if (isSubmitted) {
        trigger();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  return (
    <div>
      <h2 className={sharedStyles.heading}>Company information</h2>
      <Card className={sharedStyles.formFieldsContainer}>
        <div className={clsx(sharedStyles.formField, styles.introducerType)}>
          <Select
            {...register('introducerType')}
            id="introducerType"
            placeholder="Choose an option"
            onChange={(e) =>
              handleChangeIntroducerType(e.target.value as TIntroducerType)
            }
            className={clsx(sharedStyles.select, {
              [sharedStyles.erroredField]: errors?.introducerType?.message,
            })}
            label="Introducer type"
          >
            <option value="accountant">Accounting / advisory firm</option>
            <option value="broker">Brokerage</option>
          </Select>
          <FieldError errorMessage={errors?.introducerType?.message} />
        </div>

        <div className={clsx(sharedStyles.formField, styles.companySearch)}>
          {isCompanySearch ? (
            <CompanyInputUK
              onChange={handleCompanySearchSelect}
              setSelectedCompany={setSelectedCompany}
              tooltip="Try using the company number if you cannot find the business you are looking for."
              inputClassName={clsx({
                [sharedStyles.erroredField]: errors?.companyName?.message,
              })}
            />
          ) : (
            <Input
              {...register('companyName')}
              id="companyName"
              type="text"
              data-testid="company-name"
              label={
                <div className={styles.labelWithTooltip}>
                  <span className={styles.labelWithTooltipText}>
                    Company name
                  </span>
                  <Tooltip content="Use company search if you are registered with Companies House." />
                </div>
              }
              placeholder="Your company name"
              className={clsx({
                [sharedStyles.erroredField]: errors?.companyName?.message,
              })}
            />
          )}
          <FieldError errorMessage={errors?.companyName?.message} />
          <Button
            variant="tertiary"
            size="small"
            className={sharedStyles.toggleCompanyInput}
            type="button"
            onClick={handleToggleCompanyInputMode}
          >
            {isCompanySearch
              ? introducerType === 'accountant'
                ? 'Manual input'
                : 'Set up as sole trader'
              : 'Use company search'}
          </Button>
        </div>
        <CommercialFinance />
        <div className={sharedStyles.formField}>
          <Input
            {...register('addressLine1')}
            id="addressLine1"
            type="text"
            label="Address line 1"
            placeholder="2 Street"
            className={clsx({
              [sharedStyles.erroredField]: errors?.addressLine1?.message,
            })}
          />
          <FieldError errorMessage={errors?.addressLine1?.message} />
        </div>
        <div className={sharedStyles.formField}>
          <Input
            {...register('addressLine2')}
            id="addressLine2"
            type="text"
            label={
              <>
                Address line 2 <span>(optional)</span>
              </>
            }
            placeholder="Unit 12"
          />
        </div>
        <div className={sharedStyles.formField}>
          <Input
            {...register('town')}
            id="town"
            type="text"
            label="Town / city"
            placeholder="London"
            className={clsx({
              [sharedStyles.erroredField]: errors?.town?.message,
            })}
          />
          <FieldError errorMessage={errors?.town?.message} />
        </div>
        <div className={sharedStyles.formField}>
          <Input
            {...register('postcode', {
              setValueAs: (text: string) => text.toUpperCase(),
            })}
            id="postcode"
            type="text"
            label="Postcode"
            placeholder="W1W 6XH"
            className={clsx(styles.uppercaseText, {
              [sharedStyles.erroredField]: errors?.postcode?.message,
            })}
          />
          <FieldError errorMessage={errors?.postcode?.message} />
        </div>
        <FileUpload
          docType="proof_of_address"
          label={DOCUMENT_UPLOAD_UK.proof_of_address.label}
          instruction={DOCUMENT_UPLOAD_UK.proof_of_address.instruction}
          selectedFile={watch('proofOfAddressFile')}
          errorMessage={errors?.proofOfAddressFile?.message}
          onChange={(file) => setValue('proofOfAddressFile', file)}
        />
        <div className={sharedStyles.formField}>
          <Input
            {...register('companyTradingName')}
            id="companyTradingName"
            data-testid="company-trading-name"
            type="text"
            label={
              <>
                Company trading name <span>(optional)</span>
              </>
            }
            placeholder="iwoca"
          />
        </div>
        <div className={sharedStyles.formField}>
          <Input
            {...register('companyWebsite')}
            id="companyWebsite"
            type="text"
            label={
              <>
                Company website <span>(optional)</span>
              </>
            }
            placeholder="www.iwoca.co.uk"
          />
        </div>
        <div className={sharedStyles.formField}>
          <Input
            {...register('commissionReportEmail')}
            id="commissionReportEmail"
            type="text"
            label={
              <div className={styles.labelWithTooltip}>
                <span className={styles.labelWithTooltipText}>
                  Commission report email address
                </span>
                <Tooltip
                  dataTestId="commission-report-email-tooltip"
                  content="A dedicated email address for receiving commission reports."
                />
              </div>
            }
            placeholder="commission@iwoca.co.uk"
            className={clsx({
              [sharedStyles.erroredField]:
                errors?.commissionReportEmail?.message,
            })}
          />
          <FieldError errorMessage={errors?.commissionReportEmail?.message} />
        </div>
        <div className={sharedStyles.formField}>
          <Input
            {...register('ICOReference')}
            id="ICOReference"
            type="text"
            label="ICO registration number"
            placeholder="Z1234567"
            className={clsx({
              [sharedStyles.erroredField]: errors?.ICOReference?.message,
            })}
          />
          <FieldError errorMessage={errors?.ICOReference?.message} />
        </div>
        {introducerType === 'broker' && (
          <div className={sharedStyles.formField}>
            <Input
              {...register('FCAReferenceNumber')}
              id="FCAReferenceNumber"
              type="text"
              label={
                <>
                  FCA reference number <span>(optional)</span>
                </>
              }
              placeholder="123456"
            />
          </div>
        )}
        <FileUpload
          docType="bank_statement"
          label={DOCUMENT_UPLOAD_UK.bank_statement.label}
          instruction={DOCUMENT_UPLOAD_UK.bank_statement.instruction}
          selectedFile={watch('bankStatementFile')}
          errorMessage={errors?.bankStatementFile?.message}
          onChange={(file) => setValue('bankStatementFile', file)}
        />
      </Card>
    </div>
  );
};
